import React, { useState } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Button,
    Divider,
    Box,
    Link,
    Chip,
    Grid,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {ReactComponent as GenerateButton} from "../../../images/generateButton.svg";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GitHubIcon from '@mui/icons-material/GitHub';
// import BitbucketIcon from '../../../images/bitbucket-icon.svg'; // You might need to add this icon

function BrowsePathPopup({ open, handleClose, text1, prDetails, provider }) {
    const navigate = useNavigate();
    const [showPopup, setShowPopup] = useState(false);
    
    // Debug logging
    // console.log("BrowsePathPopup - prDetails:", prDetails);
    // console.log("BrowsePathPopup - text1:", text1);
    // console.log("BrowsePathPopup - provider:", provider);

    const handlePopup = () => {
        console.log("Popup visibility");
        handleClose();
        navigate("/landingpage" )
    };

    const openPullRequest = () => {
        const prUrl = prDetails?.pullRequestUrl || prDetails?.prUrl || prDetails?.url;
        if (prUrl) {
            window.open(prUrl, '_blank');
        }
    };

    // Determine the Git provider and get appropriate icon and base URL
    const getGitProviderInfo = () => {
        const repository = prDetails?.repository;
        const explicitProvider = provider || prDetails?.provider;
        
        // Priority: explicit provider prop > prDetails.provider > repository URL detection
        if (explicitProvider) {
            if (explicitProvider.toLowerCase() === 'bitbucket') {
                return {
                    name: 'Bitbucket',
                    // icon: <img src={BitbucketIcon} alt="Bitbucket" style={{ width: 20, height: 20, marginRight: 8 }} />,
                    baseUrl: 'https://bitbucket.org'
                };
            } else {
                return {
                    name: 'GitHub',
                    icon: <GitHubIcon sx={{ mr: 1, fontSize: 20 }} />,
                    baseUrl: 'https://github.com'
                };
            }
        }
        
        // Fallback: detect from repository URL
        if (repository) {
            if (repository.includes('bitbucket.org')) {
                return {
                    name: 'Bitbucket',
                    // icon: <img src={BitbucketIcon} alt="Bitbucket" style={{ width: 20, height: 20, marginRight: 8 }} />,
                    baseUrl: 'https://bitbucket.org'
                };
            }
        }
        
        // Default to GitHub
        return {
            name: 'GitHub',
            icon: <GitHubIcon sx={{ mr: 1, fontSize: 20 }} />,
            baseUrl: 'https://github.com'
        };
    };

    const gitProvider = getGitProviderInfo();

    // Construct PR URL based on provider
    const getPullRequestUrl = () => {
        if (!gitProvider || !prDetails?.repository) return null;

        const prNumber = prDetails.pullRequestNumber || prDetails.prNumber || prDetails.number;
        if (!prNumber) return null;

        const repoPath = prDetails.repository.replace(`${gitProvider.baseUrl}/`, '');
        
        if (gitProvider.name === 'Bitbucket') {
            return `${gitProvider.baseUrl}/${repoPath}/pull-requests/${prNumber}`;
        } else {
            // GitHub
            return `${gitProvider.baseUrl}/${repoPath}/pull/${prNumber}`;
        }
    };

    const prUrl = getPullRequestUrl();

    return (
        <>
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: "8px",
                    minWidth: "500px",
                    padding: "20px",
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    paddingBottom: "10px",
                    fontFamily: "Roboto, sans-serif",
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    color: "#4caf50"
                }}
            >
                <CheckCircleOutlineIcon sx={{ mr: 1 }} />
                Success
            </DialogTitle>

            <DialogContent sx={{ paddingBottom: "20px" }}>
                <Typography
                    variant="body1"
                    sx={{
                        fontSize: "16px",
                        fontFamily: "Roboto, sans-serif",
                        color: "#333",
                        mb: 2
                    }}
                >
                    {text1}
                </Typography>

                {prDetails && (
                    <Box sx={{ mt: 3, bgcolor: "#f8f8f8", p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                            {gitProvider ? gitProvider.icon : <GitHubIcon sx={{ mr: 1, fontSize: 20 }} />}
                            Pull Request Details
                            {gitProvider && (
                                <Chip 
                                    label={gitProvider.name} 
                                    size="small" 
                                    color={gitProvider.name === 'Bitbucket' ? 'primary' : 'default'}
                                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                />
                            )}
                        </Typography>
                        
                        <Grid container spacing={2}>
                            {/* PR Number - try different possible field names */}
                            {(prDetails.pullRequestNumber || prDetails.prNumber || prDetails.number) && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        Pull Request Number:
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium" color="primary">
                                        #{prDetails.pullRequestNumber || prDetails.prNumber || prDetails.number}
                                    </Typography>
                                </Grid>
                            )}
                            
                            {/* View Pull Request Button */}
                            {prUrl && (
                                <Grid item xs={12} sx={{ mt: 1 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<OpenInNewIcon />}
                                        onClick={() => window.open(prUrl, '_blank')}
                                        size="small"
                                        sx={{ textTransform: 'none' }}
                                    >
                                        View Pull Request on {gitProvider?.name || 'Git'}
                                    </Button>
                                </Grid>
                            )}
                            
                            {prDetails.repository && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        Repository:
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {prDetails.repository}
                                    </Typography>
                                </Grid>
                            )}
                            
                            {prDetails.branch && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        Branch:
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {prDetails.branch}
                                    </Typography>
                                </Grid>
                            )}
                            
                            {prDetails.executionTime && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        Execution Time:
                                    </Typography>
                                    <Typography variant="body1">
                                        {Math.round(prDetails.executionTime / 1000)} seconds
                                    </Typography>
                                </Grid>
                            )}
                            
                            {/* Provider info if available */}
                            {(provider || prDetails.provider) && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        Git Provider:
                                    </Typography>
                                    <Chip 
                                        label={(provider || prDetails.provider).charAt(0).toUpperCase() + (provider || prDetails.provider).slice(1)} 
                                        size="small" 
                                        color={(provider || prDetails.provider) === 'bitbucket' ? 'primary' : 'default'}
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ justifyContent: "flex-end", padding: "10px 24px" }}>
                <Button
                    onClick={handlePopup}
                    variant="contained"
                    color="primary"
                    startIcon={<GenerateButton />}
                    sx={{
                        textTransform: "none",
                        fontSize: "14px",
                        fontFamily: "Roboto, sans-serif",
                        padding: "6px 16px",
                    }}
                >
                    OK
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}

export default BrowsePathPopup;
